/**
 * Utility functions for city name normalization and matching
 */

/**
 * Normalizza il nome di una città per il matching
 * Rimuove caratteri speciali, converte in lowercase, gestisce apostrofi e trattini
 */
export function normalizeCity(city: string): string {
  if (!city) return '';
  
  return city
    .toLowerCase()
    .trim()
    // Rimuovi apostrofi
    .replace(/'/g, '')
    .replace(/'/g, '') // apostrofo curvo
    .replace(/`/g, '')
    // Sostituisci trattini con spazi
    .replace(/-/g, ' ')
    // Rimuovi caratteri speciali extra
    .replace(/[^\w\s]/g, '')
    // Normalizza spazi multipli
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Genera varianti di una città per il matching
 * Include versioni con e senza apostrofi, con trattini e spazi
 */
export function generateCityVariants(city: string): string[] {
  if (!city) return [];
  
  const normalized = normalizeCity(city);
  const variants = new Set<string>();
  
  // Versione normalizzata
  variants.add(normalized);
  
  // Versione originale (lowercase)
  variants.add(city.toLowerCase().trim());
  
  // Versione con trattini al posto degli spazi
  variants.add(normalized.replace(/\s+/g, '-'));
  
  // Versione senza spazi
  variants.add(normalized.replace(/\s+/g, ''));
  
  // Gestione speciale per nomi con "sant", "santa", "santo"
  const cityLower = city.toLowerCase();
  if (cityLower.includes('sant-') || cityLower.includes('santa-') || cityLower.includes('santo-')) {
    // Converti "sant-" in "sant'" e varianti
    const withApostrophe = cityLower
      .replace(/sant-/g, "sant'")
      .replace(/santa-/g, "santa ")
      .replace(/santo-/g, "santo ")
      .replace(/-/g, ' ');
    variants.add(withApostrophe);
    
    // Versione normalizzata con apostrofo
    const normalizedWithApostrophe = normalizeCity(withApostrophe);
    variants.add(normalizedWithApostrophe);
    
    // Versione con apostrofo ma senza altri spazi
    const apostropheNoSpaces = withApostrophe.replace(/\s+/g, '');
    variants.add(apostropheNoSpaces);
  }
  
  // Se contiene spazi, aggiungi versioni con apostrofi
  if (normalized.includes(' ')) {
    const words = normalized.split(' ');
    if (words.length >= 2) {
      // Versione con apostrofo dopo la prima parola
      variants.add(words[0] + "'" + words.slice(1).join(''));
      variants.add(words[0] + "'" + words.slice(1).join(' '));
      
      // Gestione speciale per "sant", "santa", "santo"
      if (words[0] === 'sant' || words[0] === 'santa' || words[0] === 'santo') {
        variants.add(words[0] + "'" + words.slice(1).join(' '));
        variants.add(words[0] + "'" + words.slice(1).join(''));
      }
    }
  }
  
  // Aggiungi varianti specifiche per nomi comuni italiani
  const commonPatterns = [
    { from: /\bsant\s+/g, to: "sant'" },
    { from: /\bsanta\s+/g, to: "santa " },
    { from: /\bsanto\s+/g, to: "santo " },
    { from: /\bsan\s+/g, to: "san " }
  ];
  
  commonPatterns.forEach(pattern => {
    const variant = normalized.replace(pattern.from, pattern.to);
    if (variant !== normalized) {
      variants.add(variant);
      variants.add(variant.replace(/\s+/g, ''));
      variants.add(variant.replace(/\s+/g, '-'));
    }
  });
  
  return Array.from(variants).filter(v => v.length > 0);
}

/**
 * Verifica se una città del database corrisponde alla città cercata
 * Usa matching flessibile ma preciso
 */
export function matchesCity(dealerCity: string, searchCity: string): boolean {
  if (!dealerCity || !searchCity) return false;
  
  const normalizedDealer = normalizeCity(dealerCity);
  const searchVariants = generateCityVariants(searchCity);
  
  // Matching esatto con una delle varianti
  for (const variant of searchVariants) {
    const normalizedVariant = normalizeCity(variant);
    if (normalizedDealer === normalizedVariant) {
      return true;
    }
  }
  
  // Matching per città composte (es. "Lido di Ostia" vs "Lido")
  // Solo se la città cercata è contenuta come parola completa
  const dealerWords = normalizedDealer.split(' ');
  const searchNormalized = normalizeCity(searchCity);
  
  // Verifica se la città cercata è una parola completa nella città del dealer
  return dealerWords.includes(searchNormalized);
}

/**
 * Converte un URL slug in nome città formattato
 * Es: "sant-ambrogio-di-valpolicella" -> "Sant'Ambrogio di Valpolicella"
 */
export function formatCityFromSlug(slug: string): string {
  if (!slug) return '';
  
  return slug
    .split('-')
    .map(word => {
      // Gestione speciale per articoli e preposizioni
      const lowerWord = word.toLowerCase();
      if (['di', 'del', 'della', 'dei', 'delle', 'da', 'dal', 'dalla', 'in', 'su', 'per'].includes(lowerWord)) {
        return lowerWord;
      }
      
      // Capitalizza la prima lettera
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    // Gestione apostrofi per nomi comuni
    .replace(/\bsant\s+/gi, "Sant'")
    .replace(/\bsanta\s+/gi, "Santa ")
    .replace(/\bsanto\s+/gi, "Santo ");
}

/**
 * Converte un nome città in URL slug
 * Es: "Sant'Ambrogio di Valpolicella" -> "sant-ambrogio-di-valpolicella"
 */
export function cityToSlug(city: string): string {
  if (!city) return '';
  
  return city
    .toLowerCase()
    .trim()
    // Rimuovi apostrofi
    .replace(/'/g, '')
    .replace(/'/g, '')
    .replace(/`/g, '')
    // Sostituisci spazi e caratteri speciali con trattini
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-')
    // Rimuovi trattini multipli
    .replace(/-+/g, '-')
    // Rimuovi trattini all'inizio e alla fine
    .replace(/^-+|-+$/g, '');
}
